/**
 * Ebene Geometrie fuer den Spielplan. Reine Mathematik, keine Renderabhaengigkeit.
 * Alle Laengen in Millimetern, Ursprung in der Brettmitte, x nach rechts, z nach unten.
 */

export interface Punkt {
  readonly x: number
  readonly z: number
}

export type Polygon = readonly Punkt[]

/** Achsparalleles Huellrechteck. */
export interface Kasten {
  readonly minX: number
  readonly maxX: number
  readonly minZ: number
  readonly maxZ: number
}

export const TAU = Math.PI * 2

export function grad(bogen: number): number {
  return ((bogen * 180) / Math.PI + 360) % 360
}

export function bogen(grad: number): number {
  return (grad * Math.PI) / 180
}

/** Winkel eines Punktes um den Ursprung, in Grad, 0 bis 360. */
export function winkelVon(p: Punkt): number {
  return grad(Math.atan2(p.z, p.x))
}

export function punkt(x: number, z: number): Punkt {
  return { x, z }
}

export function addiere(a: Punkt, b: Punkt): Punkt {
  return { x: a.x + b.x, z: a.z + b.z }
}

export function skaliere(a: Punkt, f: number): Punkt {
  return { x: a.x * f, z: a.z * f }
}

export function laenge(a: Punkt): number {
  return Math.hypot(a.x, a.z)
}

export function normiere(a: Punkt): Punkt {
  const l = laenge(a)
  return l === 0 ? { x: 0, z: 0 } : { x: a.x / l, z: a.z / l }
}

/** Um 90 Grad nach rechts gedreht (in Fahrtrichtung: die rechte Strassenseite). */
export function rechts(a: Punkt): Punkt {
  return { x: -a.z, z: a.x }
}

export function huelle(punkte: Polygon): Kasten {
  let minX = Infinity
  let maxX = -Infinity
  let minZ = Infinity
  let maxZ = -Infinity
  for (const p of punkte) {
    if (p.x < minX) minX = p.x
    if (p.x > maxX) maxX = p.x
    if (p.z < minZ) minZ = p.z
    if (p.z > maxZ) maxZ = p.z
  }
  return { minX, maxX, minZ, maxZ }
}

export function kastenEnthaelt(aussen: Kasten, innen: Kasten, toleranz = 1e-9): boolean {
  return (
    innen.minX >= aussen.minX - toleranz &&
    innen.maxX <= aussen.maxX + toleranz &&
    innen.minZ >= aussen.minZ - toleranz &&
    innen.maxZ <= aussen.maxZ + toleranz
  )
}

export function kastenUeberlappt(a: Kasten, b: Kasten, toleranz = 1e-9): boolean {
  return (
    a.minX < b.maxX - toleranz &&
    b.minX < a.maxX - toleranz &&
    a.minZ < b.maxZ - toleranz &&
    b.minZ < a.maxZ - toleranz
  )
}

/** Punkt-in-Polygon nach dem Strahlensatz (ungerade Zahl von Schnitten = innen). */
export function imPolygon(p: Punkt, poly: Polygon): boolean {
  let drin = false
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!
    const b = poly[j]!
    const schneidet = a.z > p.z !== b.z > p.z
    if (schneidet && p.x < ((b.x - a.x) * (p.z - a.z)) / (b.z - a.z) + a.x) drin = !drin
  }
  return drin
}

/** Kuerzester Abstand eines Punktes zum Rand eines Polygons. */
export function abstandZumRand(p: Punkt, poly: Polygon): number {
  let min = Infinity
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    min = Math.min(min, abstandZurStrecke(p, poly[j]!, poly[i]!))
  }
  return min
}

export function abstandZurStrecke(p: Punkt, a: Punkt, b: Punkt): number {
  const dx = b.x - a.x
  const dz = b.z - a.z
  const l2 = dx * dx + dz * dz
  if (l2 === 0) return Math.hypot(p.x - a.x, p.z - a.z)
  let t = ((p.x - a.x) * dx + (p.z - a.z) * dz) / l2
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(p.x - (a.x + t * dx), p.z - (a.z + t * dz))
}

export function flaeche(poly: Polygon): number {
  let s = 0
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    s += (poly[j]!.x + poly[i]!.x) * (poly[j]!.z - poly[i]!.z)
  }
  return Math.abs(s) / 2
}

/**
 * Ein gedrehtes Rechteck: der Grundriss eines Bauplatzes.
 * `mitte` ist der Ankerpunkt, `drehung` der Winkel im Bogenmass um die y-Achse.
 * `breite` liegt entlang der gedrehten x-Achse, `tiefe` entlang der gedrehten z-Achse.
 */
export interface Grundriss {
  readonly mitte: Punkt
  readonly breite: number
  readonly tiefe: number
  readonly drehung: number
}

/** Die vier Eckpunkte eines Grundrisses, gegen den Uhrzeigersinn. */
export function grundrissEcken(g: Grundriss): Polygon {
  const c = Math.cos(g.drehung)
  const s = Math.sin(g.drehung)
  const hb = g.breite / 2
  const ht = g.tiefe / 2
  const lokal: Punkt[] = [
    { x: -hb, z: -ht },
    { x: hb, z: -ht },
    { x: hb, z: ht },
    { x: -hb, z: ht },
  ]
  return lokal.map((p) => ({
    x: g.mitte.x + p.x * c - p.z * s,
    z: g.mitte.z + p.x * s + p.z * c,
  }))
}

/**
 * Ueberlappen sich zwei konvexe Polygone? Trennachsensatz.
 * Beruehrung mit weniger als `abstand` Millimetern gilt bereits als Ueberlappung.
 */
export function konvexUeberlappt(a: Polygon, b: Polygon, abstand = 0): boolean {
  for (const poly of [a, b]) {
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const kante = { x: poly[i]!.x - poly[j]!.x, z: poly[i]!.z - poly[j]!.z }
      const achse = normiere({ x: -kante.z, z: kante.x })
      let aMin = Infinity
      let aMax = -Infinity
      let bMin = Infinity
      let bMax = -Infinity
      for (const p of a) {
        const s = p.x * achse.x + p.z * achse.z
        if (s < aMin) aMin = s
        if (s > aMax) aMax = s
      }
      for (const p of b) {
        const s = p.x * achse.x + p.z * achse.z
        if (s < bMin) bMin = s
        if (s > bMax) bMax = s
      }
      if (aMax < bMin + abstand || bMax < aMin + abstand) return false
    }
  }
  return true
}
